import { FunctionComponent, useMemo } from "react"

import { useParams } from "react-router-dom"
import { RootState, useTypedSelector } from "../../../app/store"
import { SimulationIds } from "../../../utils/constants"
import { TutorialConfig } from "../types/tutorial-config"
import ConfiguredTutorial from "./configured-tutorial"

// Import JSON configs for testing (will be replaced by API delivery later)
import coastareaConfig from "../configs/coastarea.json"
import croFashionRetailConfig from "../configs/cro-fashion-retail.json"
import rootsAndShootsConfig from "../configs/roots-and-shoots.json"
import silverStreakConfig from "../configs/silver-streak.json"
import youngMedicalStudentConfig from "../configs/young-medical-student.json"

const parseConfigField = (
  field: string | TutorialConfig | null | undefined,
): TutorialConfig | null => {
  if (!field) return null
  if (typeof field === "string") {
    try {
      return JSON.parse(field) as TutorialConfig
    } catch (e) {
      console.warn("[Tutorial] Failed to parse tutorialConfig string:", e)
      return null
    }
  }
  return field
}

/**
 * Tutorial Router
 *
 * Routes to the appropriate tutorial component based on the simulation ID.
 * Uses config-driven tutorials loaded from bundled JSON or API-delivered configs.
 */
const SimulationTutorial: FunctionComponent = () => {
  const { testId: testIdParam } = useParams()
  const testId = Number(testIdParam)

  // Get choices and information (tree categories) from simulation state for tree node resolution
  // Choices contains decision/action nodes, information contains "Information Search" nodes
  const { choices, information, testData } = useTypedSelector((state: RootState) => ({
    choices: state.simulationSlice.choices,
    information: state.simulationSlice.information,
    testData: state.simulationSlice.testData,
  }))

  // Combine choices and information for tree node resolution
  // Tutorial configs may reference nodes from either tree
  const allCategories = useMemo(() => {
    return [...choices, ...information]
  }, [choices, information])

  // Try to get tutorialConfig:
  //   1. Per-assignment override on testData[0].tutorialConfig (one-off custom)
  //   2. Sim-level override on tests.tutorialConfig (authored via builder; comes back
  //      either as `tutorialConfig` or `simulationTutorialConfig` depending on payload)
  //   3. Bundled JSON shipped with the app
  const tutorialConfig = useMemo((): TutorialConfig | null => {
    if (testData && testData.length > 0) {
      const test = testData[0] as {
        tutorialConfig?: string | TutorialConfig | null
        simulationTutorialConfig?: string | TutorialConfig | null
      }
      const fromAssignment = parseConfigField(test.tutorialConfig)
      if (fromAssignment) {
        return fromAssignment
      }
      const fromSim = parseConfigField(test.simulationTutorialConfig)
      if (fromSim) {
        return fromSim
      }
    }

    // Fall back to bundled JSON configs
    const bundledConfigs: Record<number, TutorialConfig> = {
      [SimulationIds.coastarea]: coastareaConfig as TutorialConfig,
      [SimulationIds.croFashionRetail]: croFashionRetailConfig as TutorialConfig,
      [SimulationIds.rootsAndShoots]: rootsAndShootsConfig as TutorialConfig,
      [SimulationIds.silverStreak]: silverStreakConfig as TutorialConfig,
      [SimulationIds.youngMedicalStudent]: youngMedicalStudentConfig as TutorialConfig,
    }

    if (bundledConfigs[testId]) {
      console.info("[Tutorial] Using bundled config for simulation", testId)
      return bundledConfigs[testId]
    }

    return null
  }, [testData, testId])

  // Wait for both config and categories before rendering
  if (!tutorialConfig || allCategories.length === 0) {
    return null
  }

  return <ConfiguredTutorial config={tutorialConfig} categories={allCategories} />
}

export default SimulationTutorial
